import React, { useRef, useEffect, useState } from 'react';
import { Send, MessageCircleWarning } from 'lucide-react';
// import { SquarePause } from 'lucide-react'; // Commented out for future stop functionality
import { useTheme } from '../../ThemeContext.tsx';

type ChatInputProps = {
    userInput: string;
    setUserInput: (value: string) => void;
    onSendMessage: (text: string) => void;
    hasStartedChat: boolean;
    onRecommendedQuestionSelect: (question: string) => void;
    isProcessing: boolean;
    // onStopProcessing: () => void; // Commented out for future stop functionality
};

const tooltipMessages = [
    'Context Window Limit: LLM "forgets" older context in long chats. Start a new chat for new topics to ensure accuracy.',
    'OpCo-level insights only. Product level queries are not supported yet.'
];

const ChatInput: React.FC<ChatInputProps> = ({ userInput, setUserInput, onSendMessage, hasStartedChat, onRecommendedQuestionSelect, isProcessing }) => {
    // onStopProcessing parameter commented out for future stop functionality
    const { colors, isMobile } = useTheme();
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [showTooltip, setShowTooltip] = useState(false);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSendMessage(userInput);
        }
    };
    useEffect(() => {
        if (!showTooltip) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (tooltipRef.current && !tooltipRef.current.contains(e.target as Node)) {
                setShowTooltip(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showTooltip]);

    return (
        <div className={`${isMobile ? 'max-w-[320px]' : 'max-w-3xl'} mx-auto transition-all duration-500 ${!hasStartedChat ? 'translate-y-[-2.5vh]' : 'translate-y-[-1vh]'}`}>
            <div className={`relative bg-white bg-opacity-30`}>
                <textarea
                    ref={textareaRef}
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type your message here..."
                    className={`w-full hide-scrollbar text-sm ${colors.bg.tertiary} border ${colors.border.secondary} rounded-lg p-3 pr-20 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 overflow-y-auto ${colors.text.primary} placeholder:${colors.text.tertiary}`}
                    rows={1}
                />

                {/* Warning Icon with Tooltip */}
                <div className="absolute right-12 bottom-2 z-10" ref={tooltipRef}>
                    <button
                        onClick={() => setShowTooltip(!showTooltip)}
                        className="p-2"
                        type="button"
                    >
                        <MessageCircleWarning size={22} className="text-[#ff7900]" />
                    </button>

                    {/* Tooltip */}
                    {showTooltip && (
                        <div className={`absolute bottom-full mb-2 w-72 bg-white border border-gray-200 rounded-lg shadow-xl p-4 z-50 ${isMobile
                            ? 'right-[-20px]'
                            : 'right-[-14px]'
                            }`}>
                            <div className="text-sm flex flex-col gap-2 text-black">
                                {tooltipMessages.map((message, index) => (
                                    <div key={index} className="flex items-start gap-2">
                                        {index === 0 ? (
                                            <span className="break-words">
                                                <span className="font-semibold">Context Window Limit</span>
                                                {': LLM "forgets" older context in long chats. Start a new chat for new topics to ensure accuracy.'}
                                            </span>
                                        ) : (
                                            <span className="break-words">
                                                <span className="font-semibold">OpCo-level insights only</span>
                                                {': Product level queries are not supported yet.'}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Send Button */}
                <button
                    onClick={() => onSendMessage(userInput)}
                    className={`absolute right-2 bottom-3 p-1.5 z-10 ${colors.interactive.primary} rounded-lg ${colors.interactive.primaryHover} disabled:opacity-50 disabled:cursor-not-allowed`}
                    disabled={!userInput.trim()}
                >
                    <Send size={18} className={colors.text.inverse} />
                </button>
            </div>
        </div>
    );
};

export default ChatInput;

